<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CeaDynamicCatalogsAPI from 'dashboard/api/ceaDynamicCatalogs';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['saved', 'cancel']);

const { t } = useI18n();

const FIELD_TYPES = [
  { value: 'text', labelKey: 'CEA_LOOKUPS.CATALOG_WIZARD.FIELD_TYPES.TEXT' },
  { value: 'number', labelKey: 'CEA_LOOKUPS.CATALOG_WIZARD.FIELD_TYPES.NUMBER' },
  { value: 'boolean', labelKey: 'CEA_LOOKUPS.CATALOG_WIZARD.FIELD_TYPES.BOOLEAN' },
  { value: 'select', labelKey: 'CEA_LOOKUPS.CATALOG_WIZARD.FIELD_TYPES.SELECT' },
];

const wizardStep = ref('info');
const saving = ref(false);

const form = ref({
  name: '',
  slug: '',
  description: '',
  fields: [],
});

const slugFromName = name =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const updateSlugFromName = () => {
  if (!form.value.slug || form.value.slug === slugFromName(form.value.name)) {
    form.value.slug = slugFromName(form.value.name);
  }
};

const addField = () => {
  form.value.fields.push({
    key: '',
    label: '',
    field_type: 'text',
    required: false,
    options: [],
  });
};

const removeField = index => {
  form.value.fields.splice(index, 1);
};

const updateFieldKey = (index, label) => {
  const field = form.value.fields[index];
  field.label = label;
  if (!field.key || field.key === slugFromName(field.label)) {
    field.key = slugFromName(label);
  }
};

const optionsText = field => (field.options || []).join(', ');

const updateOptions = (field, value) => {
  field.options = value
    .split(',')
    .map(option => option.trim())
    .filter(Boolean);
};

const canProceedFromInfo = computed(
  () => form.value.name.trim() && form.value.slug.trim()
);

const canProceedFromFields = computed(() =>
  form.value.fields.every(
    field =>
      field.key.trim() &&
      field.label.trim() &&
      (field.field_type !== 'select' || field.options.length > 0)
  )
);

const canSubmit = computed(() => {
  if (!canProceedFromInfo.value) return false;
  return canProceedFromFields.value;
});

const goToFields = () => {
  if (!canProceedFromInfo.value) return;
  wizardStep.value = 'fields';
};

const goToReview = () => {
  if (!canProceedFromInfo.value || !canProceedFromFields.value) return;
  wizardStep.value = 'review';
};

const submit = async () => {
  if (!canSubmit.value) return;

  saving.value = true;
  try {
    const response = await CeaDynamicCatalogsAPI.createCatalog({
      name: form.value.name.trim(),
      slug: form.value.slug.trim(),
      description: form.value.description.trim(),
      active: true,
      fields_schema: form.value.fields.map(field => ({
        key: field.key.trim(),
        label: field.label.trim(),
        field_type: field.field_type,
        required: field.required,
        options: field.field_type === 'select' ? field.options : [],
      })),
    });
    useAlert(t('CEA_LOOKUPS.CATALOG_WIZARD.CREATE_SUCCESS'));
    emit('saved', response.data);
  } catch {
    useAlert(t('CEA_LOOKUPS.CATALOG_WIZARD.CREATE_ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl gap-6 p-6">
    <div class="flex flex-col gap-1">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex gap-2">
      <span
        class="px-3 py-1 text-xs font-medium rounded-full"
        :class="
          wizardStep === 'info'
            ? 'bg-n-brand text-white'
            : 'bg-n-solid-3 text-n-slate-11'
        "
      >
        1. {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.STEPS.INFO') }}
      </span>
      <span
        class="px-3 py-1 text-xs font-medium rounded-full"
        :class="
          wizardStep === 'fields'
            ? 'bg-n-brand text-white'
            : 'bg-n-solid-3 text-n-slate-11'
        "
      >
        2. {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.STEPS.FIELDS') }}
      </span>
      <span
        class="px-3 py-1 text-xs font-medium rounded-full"
        :class="
          wizardStep === 'review'
            ? 'bg-n-brand text-white'
            : 'bg-n-solid-3 text-n-slate-11'
        "
      >
        3. {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.STEPS.REVIEW') }}
      </span>
    </div>

    <div v-if="wizardStep === 'info'" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.NAME') }} *
        </label>
        <Input
          v-model="form.name"
          :placeholder="$t('CEA_LOOKUPS.CATALOG_WIZARD.NAME_PLACEHOLDER')"
          @update:model-value="updateSlugFromName"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.SLUG') }} *
        </label>
        <Input
          v-model="form.slug"
          :placeholder="$t('CEA_LOOKUPS.CATALOG_WIZARD.SLUG_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.CATALOG_DESCRIPTION') }}
        </label>
        <textarea
          v-model="form.description"
          rows="3"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          :placeholder="
            $t('CEA_LOOKUPS.CATALOG_WIZARD.CATALOG_DESCRIPTION_PLACEHOLDER')
          "
        />
      </div>
    </div>

    <div v-else-if="wizardStep === 'fields'" class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELDS_HINT') }}
      </p>

      <div
        v-for="(field, index) in form.fields"
        :key="index"
        class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD') }} {{ index + 1 }}
          </span>
          <Button
            xs
            ruby
            faded
            icon="i-lucide-trash-2"
            :label="$t('CEA_LOOKUPS.CATALOG_WIZARD.REMOVE_FIELD')"
            @click="removeField(index)"
          />
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_LABEL') }} *
            </label>
            <Input
              :model-value="field.label"
              :placeholder="$t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_LABEL_PLACEHOLDER')"
              @update:model-value="value => updateFieldKey(index, value)"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_KEY') }} *
            </label>
            <Input
              v-model="field.key"
              :placeholder="$t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_KEY_PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_TYPE') }}
            </label>
            <select
              v-model="field.field_type"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12"
            >
              <option
                v-for="type in FIELD_TYPES"
                :key="type.value"
                :value="type.value"
              >
                {{ $t(type.labelKey) }}
              </option>
            </select>
          </div>
          <div class="flex items-end gap-2 pb-2">
            <input
              :id="`field-required-${index}`"
              v-model="field.required"
              type="checkbox"
              class="rounded border-n-weak"
            />
            <label
              :for="`field-required-${index}`"
              class="text-sm text-n-slate-12"
            >
              {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_REQUIRED') }}
            </label>
          </div>
        </div>

        <div v-if="field.field_type === 'select'" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_OPTIONS') }}
          </label>
          <Input
            :model-value="optionsText(field)"
            :placeholder="$t('CEA_LOOKUPS.CATALOG_WIZARD.FIELD_OPTIONS_PLACEHOLDER')"
            @update:model-value="value => updateOptions(field, value)"
          />
        </div>
      </div>

      <Button
        faded
        slate
        icon="i-lucide-plus"
        :label="$t('CEA_LOOKUPS.CATALOG_WIZARD.ADD_FIELD')"
        @click="addField"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <div class="p-4 border rounded-lg border-n-weak bg-n-solid-2">
        <p class="text-sm font-medium text-n-slate-12">{{ form.name }}</p>
        <p class="text-xs font-mono text-n-slate-11">{{ form.slug }}</p>
        <p v-if="form.description" class="mt-2 text-sm text-n-slate-11">
          {{ form.description }}
        </p>
      </div>

      <div v-if="form.fields.length" class="flex flex-col gap-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.REVIEW_FIELDS') }}
        </p>
        <div
          v-for="field in form.fields"
          :key="field.key"
          class="flex items-center justify-between px-3 py-2 text-sm rounded-lg bg-n-solid-3"
        >
          <span class="text-n-slate-12">{{ field.label }}</span>
          <span class="text-xs text-n-slate-11">
            {{ field.field_type }}
            {{
              field.required
                ? `(${$t('CEA_LOOKUPS.CATALOG_WIZARD.REQUIRED')})`
                : ''
            }}
          </span>
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-11">
        {{ $t('CEA_LOOKUPS.CATALOG_WIZARD.NO_CUSTOM_FIELDS') }}
      </p>
    </div>

    <div class="flex items-center justify-between gap-3 pt-2 border-t border-n-weak">
      <Button
        faded
        slate
        :label="$t('CEA_LOOKUPS.FORM.CANCEL')"
        @click="emit('cancel')"
      />

      <div class="flex gap-2">
        <Button
          v-if="wizardStep !== 'info'"
          faded
          slate
          :label="$t('CEA_LOOKUPS.CATALOG_WIZARD.BACK')"
          @click="wizardStep = wizardStep === 'review' ? 'fields' : 'info'"
        />
        <Button
          v-if="wizardStep === 'info'"
          :label="$t('CEA_LOOKUPS.CATALOG_WIZARD.NEXT')"
          :disabled="!canProceedFromInfo"
          @click="goToFields"
        />
        <Button
          v-else-if="wizardStep === 'fields'"
          :label="$t('CEA_LOOKUPS.CATALOG_WIZARD.NEXT')"
          :disabled="!canProceedFromFields"
          @click="goToReview"
        />
        <Button
          v-else
          :label="$t('CEA_LOOKUPS.CATALOG_WIZARD.CREATE')"
          :is-loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
